import classNames from 'classnames'
import React, { PropsWithChildren, useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useProducts } from '@pages/Signup/hooks/useProducts'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useSessionEntity } from '@context/EntityContext'
import { useLegalLinksContext } from '@context/LegalLinksContext'
import { useLossPercentagesReadContext } from '@context/LossPercentagesContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { Caption1 } from '@ui/Typography'
import { Link, LinkSize } from '@ui/components/Link'

import { isProductRegistered } from '@utils/AccountAccess/accountProductStatuses'
import {
  TickmillCompaniesEnum,
  isTickmillASType,
  isTickmillEUType,
  isTickmillGenericType,
  isTickmillPartnerType,
  isTickmillSCType,
  isTickmillUKType,
} from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'

import { TickmillProductType } from '@model/TickmillProductType'

import { useHomePageUrl } from '@hooks/useHomePageUrl'
import { useLinksConfig } from '@hooks/useLinksConfig'

import {
  BankTransferPaymentIcon,
  DotPayPaymentIcon,
  MasterCardPaymentIcon,
  NetellerPaymentIcon,
  PaySafePaymentIcon,
  RapidPaymentIcon,
  SkrillPaymentIcon,
  SofortPaymentIcon,
  VisaPaymentIcon,
} from '@assets/PSPLogos/PaymentIcons'

import { Box } from '@shared/components/Box'

import styles from './Footer.module.scss'

const COPYRIGHT_YEAR = new Date().getFullYear()
const ENTITY_NAMES = {
  UK: 'Tickmill UK Ltd',
  EU: 'Tickmill Europe Ltd',
}

const FooterTextPerEntity: React.FC = () => {
  const { t } = useTranslation()

  const tickmillCompany = useSessionEntity()
  const { links } = useLinksConfig()
  const { docLinks } = useLegalLinksContext()
  const { percentages } = useLossPercentagesReadContext()
  const locale = useSessionLanguage()
  const ukFooterProducts = useUKFooterProducts()

  const isUkCFDFooter =
    ukFooterProducts.includes(TickmillProductType.CFD) &&
    !ukFooterProducts.includes(TickmillProductType.ETD)

  const isUkETDFooter =
    ukFooterProducts.includes(TickmillProductType.ETD) &&
    !ukFooterProducts.includes(TickmillProductType.CFD)

  if (isTickmillGenericType(tickmillCompany)) {
    return (
      <>
        <FooterText>
          {t('Footer.Tickmill is the trading name of Tickmill Group of companies.')}
        </FooterText>
        <FooterText>{t('Footer.First Desc GENERIC')}</FooterText>
        <FooterText>{t('Footer.Clients')}</FooterText>
        <FooterText>
          <strong>{t('Footer.High Risk Warning')}:</strong>{' '}
          {t(
            'Footer.Trading Contracts for Difference (CFDs) on margin carries a high level of risk'
          )}
        </FooterText>
        <LocaleSpecificClause locale={locale} />
        <FooterCopyright>{t('Footer.Copyright', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillSCType(tickmillCompany)) {
    return (
      <>
        <FooterText>{t('Footer.First Desc SC Combined')}</FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning SC Combined'
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_SC.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillEUType(tickmillCompany)) {
    return (
      <>
        <FooterText>
          <Trans
            i18nKey='Footer.First Desc EU Combined'
            components={[
              <FooterLink
                url={docLinks?.cysec}
                fallback={links.footer.TICKMILL_EU.CySEC}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>

        <FooterText>
          <strong>{t('Footer.High Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning EU Combined'
            values={{ risk: percentages?.percentage ?? '', entity: ENTITY_NAMES.EU }}
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_EU.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright EU', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillASType(tickmillCompany)) {
    return (
      <>
        <FooterText>{t('Footer.First Desc SC Combined')}</FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning SC Combined'
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_AS.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillPartnerType(tickmillCompany)) {
    return (
      <>
        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          {t('Footer.All financial products traded on margin carry')}{' '}
          <FooterLink
            url={docLinks?.riskDisclosure}
            fallback={links.footer.TICKMILL_PA.riskDisclosure}
            label={t('Footer.Risk Disclosure')}
            size='sm'
            noDivider
          />
          .
        </FooterText>
        <FooterText>{t('Footer.Second Desc PA')}</FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>
          {t('Footer.Copyright', {
            year: COPYRIGHT_YEAR,
          })}
        </FooterCopyright>
      </>
    )
  }

  if (isTickmillUKType(tickmillCompany) && isUkCFDFooter) {
    return (
      <>
        <FooterText>
          <Trans
            i18nKey='Footer.First Desc UK Combined'
            components={[
              <FooterLink
                url={docLinks?.financialServicesRegister}
                fallback={links.footer.TICKMILL_UK.financialConductAuthority}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning CFD Combined'
            values={{ lossPercentage: percentages?.percentage ?? '' }}
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_UK.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright UK', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillUKType(tickmillCompany) && isUkETDFooter) {
    return (
      <>
        <FooterText>
          <Trans
            i18nKey='Footer.First Desc UK Combined'
            components={[
              <FooterLink
                url={docLinks?.financialServicesRegister}
                fallback={links.footer.TICKMILL_UK.financialConductAuthority}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning ETD Combined'
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_UK.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright UK', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  if (isTickmillUKType(tickmillCompany)) {
    return (
      <>
        <FooterText>
          <Trans
            i18nKey='Footer.First Desc UK Combined'
            components={[
              <FooterLink
                url={docLinks?.financialServicesRegister}
                fallback={links.footer.TICKMILL_UK.financialConductAuthority}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning CFD Combined'
            values={{ lossPercentage: percentages?.percentage ?? '' }}
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_UK.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>

        <FooterText>
          <strong>{t('Footer.Risk Warning')}:</strong>{' '}
          <Trans
            i18nKey='Footer.Risk Warning ETD Combined'
            components={[
              <FooterLink
                url={docLinks?.riskDisclosure}
                fallback={links.footer.TICKMILL_UK.riskDisclosure}
                size='sm'
                noDivider
              />,
            ]}
          />
        </FooterText>
        <LocaleSpecificClause locale={locale} />

        <FooterCopyright>{t('Footer.Copyright UK', { year: COPYRIGHT_YEAR })}</FooterCopyright>
      </>
    )
  }

  return null
}

const useTermsAndConditionsLink = () => {
  const homePageUrl = useHomePageUrl()
  return `${homePageUrl}/terms-conditions`
}

const AppLinksPerEntity: React.FC = () => {
  const { t } = useTranslation()

  const tickmillCompany = useSessionEntity()
  const { links } = useLinksConfig()
  const { docLinks } = useLegalLinksContext()
  const termsAndConditionsLink = useTermsAndConditionsLink()
  const [auth] = useContext(AuthSessionContext)
  const ukFooterProducts = useUKFooterProducts()

  const isUkCFDFooter =
    ukFooterProducts.includes(TickmillProductType.CFD) &&
    !ukFooterProducts.includes(TickmillProductType.ETD)

  const isUkETDFooter =
    ukFooterProducts.includes(TickmillProductType.ETD) &&
    !ukFooterProducts.includes(TickmillProductType.CFD)

  if (auth) {
    return null
  }

  if (isTickmillGenericType(tickmillCompany)) {
    return <FooterTextPerEntity />
  }

  if (isTickmillSCType(tickmillCompany)) {
    return (
      <>
        <div className='level-left'>
          <FooterLink
            url={docLinks?.termsOfBusinessWebsite}
            fallback={links.footer.TICKMILL_SC.termsOfBusiness}
            label={t('Terms of Business')}
            noDivider
          />
        </div>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  if (isTickmillEUType(tickmillCompany)) {
    return (
      <>
        <div className='level-left'>
          <FooterLink
            url={docLinks?.clientAgreement}
            fallback={links.footer.TICKMILL_EU.clientAgreement}
            label={t('Footer.Client Agreement')}
          />
          <FooterLink
            url={docLinks?.privacyPolicy}
            fallback={links.footer.TICKMILL_EU.privacyPolicy}
            label={t('Footer.Privacy Policy')}
            isLast
          />
        </div>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  if (isTickmillASType(tickmillCompany)) {
    return (
      <>
        <div className='level-left'>
          <div className={classNames('level-item', 'is-hidden-touch', styles.copyright)}>
            {t('Footer.Copyright', {
              year: COPYRIGHT_YEAR,
            })}
          </div>
        </div>
        <div className='level-left'>
          <FooterLink
            url={docLinks?.termsOfBusinessWebsite}
            fallback={links.footer.TICKMILL_AS.termsOfBusiness}
            label={t('Terms of Business')}
            noDivider
          />
        </div>
        <FooterText>{t('Footer.First Desc SC Combined')}</FooterText>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  if (isTickmillPartnerType(tickmillCompany)) {
    return (
      <>
        <div className='level-left'>
          <FooterLink
            url={docLinks?.websiteTermsAndConditions}
            fallback={termsAndConditionsLink}
            label={t('Footer.Website Terms & Conditions')}
          />
          <FooterLink
            url={docLinks?.termsOfBusinessWebsite}
            fallback={links.footer.TICKMILL_PA.termsOfBusiness}
            label={t('Terms of Business')}
          />
          <FooterLink
            url={docLinks?.riskDisclosure}
            fallback={links.footer.TICKMILL_PA.riskDisclosure}
            label={t('Footer.Risk Disclosure')}
            isLast
          />
        </div>
        <FooterText>{t('Footer.First Desc PA')}</FooterText>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  if (isUkCFDFooter) {
    return (
      <>
        <div className='level-left'>
          <FooterLink
            url='https://www.tickmill.com/files/documents/legal-documents/fca/terms-of-business.pdf'
            label={t('Footer.Terms of Business (Forex & CFDs)')}
          />
          <FooterLink
            url={docLinks?.bestExecutionPolicy}
            fallback={links.footer.TICKMILL_UK.bestExecutionPolicy}
            label={t('Footer.Best Execution Policy')}
            isLast
          />
        </div>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  if (isUkETDFooter) {
    return (
      <>
        <div className='level-left'>
          <FooterLink
            url='https://www.tickmill.com/files/documents/legal-documents/fca/terms-of-business-futures-and-options.pdf'
            label={t('Footer.Terms of Business (Futures & Options)')}
          />
          <FooterLink
            url={docLinks?.bestExecutionPolicy}
            fallback={links.footer.TICKMILL_UK_ETD.bestExecutionPolicy}
            label={t('Footer.Best Execution Policy')}
            isLast
          />
        </div>
        <Box mt='x16'>
          <FooterTextPerEntity />
        </Box>
      </>
    )
  }

  return (
    <>
      <div className='level-left'>
        <FooterLink
          url='https://www.tickmill.com/files/documents/legal-documents/fca/terms-of-business.pdf'
          label={t('Footer.Terms of Business (Forex & CFDs)')}
        />
        <FooterLink
          url='https://www.tickmill.com/files/documents/legal-documents/fca/terms-of-business-futures-and-options.pdf'
          label={t('Footer.Terms of Business (Futures & Options)')}
        />
        <FooterLink
          url={docLinks?.bestExecutionPolicy}
          fallback={links.footer.TICKMILL_UK_ETD.bestExecutionPolicy}
          label={t('Footer.Best Execution Policy')}
          isLast
        />
      </div>
      <Box mt='x16'>
        <FooterTextPerEntity />
      </Box>
    </>
  )
}

interface FooterTextProps extends PropsWithChildren {
  className?: string
  dangerouslySetInnerHTML?: {
    __html: string
  }
}

const FooterText: React.FC<FooterTextProps> = ({
  children,
  className,
  dangerouslySetInnerHTML,
}) => (
  <>
    <Caption1
      isParagraph
      className={classNames(styles.text, className)}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </Caption1>
    <br />
  </>
)

const FooterCopyright: React.FC<PropsWithChildren> = ({ children }) => (
  <div className='level-left'>
    <div className={classNames('level-item')}>
      <Caption1 isParagraph className={styles.text}>
        {children}
      </Caption1>
    </div>
  </div>
)

interface FooterLinkProps {
  url?: string | null
  fallback?: string
  label?: string
  isLast?: boolean
  noDivider?: boolean
  children?: React.ReactNode
  size?: LinkSize
}

const FooterLink: React.FC<FooterLinkProps> = (props) => {
  const { url, label, isLast, fallback, noDivider, children, size = 'lg' } = props
  const isMobile = useWindowResize()
  const href = url ?? fallback

  if (!href) {
    return null
  }
  if (!url && fallback) {
    // Enable if you need to debug missing URLs
    // console.log('Fallback URL is beeing used for footer links for: ' + label, { url, fallback })
  }

  return (
    <>
      <Link href={href} size={size}>
        {children ?? label}
      </Link>
      {!noDivider && (
        <>
          {isMobile && <br />}
          {isMobile && <br />}
          {isLast ? null : <>{!isMobile && <span className={styles.divider}>|</span>}</>}
        </>
      )}
    </>
  )
}

const PaymentProviderLogoPerEntity = () => {
  const tickmillEntity = useSessionEntity()

  if (isTickmillEUType(tickmillEntity)) {
    return (
      <>
        <BankTransferPaymentIcon />
        <VisaPaymentIcon />
        <MasterCardPaymentIcon />
        <NetellerPaymentIcon />
        <DotPayPaymentIcon />
        <SkrillPaymentIcon />
      </>
    )
  }

  if (isTickmillASType(tickmillEntity)) {
    // thailand internet banking
    return <BankTransferPaymentIcon />
  }

  if (isTickmillSCType(tickmillEntity)) {
    // fasa pay
    // union pay
    // ngan luong
    return (
      <>
        <BankTransferPaymentIcon />
        <VisaPaymentIcon />
        <MasterCardPaymentIcon />
        <DotPayPaymentIcon />
        <NetellerPaymentIcon />
        <SkrillPaymentIcon />
        <PaySafePaymentIcon />
        <SofortPaymentIcon />
        <RapidPaymentIcon />
      </>
    )
  }

  if (isTickmillPartnerType(tickmillEntity)) {
    return (
      <>
        <BankTransferPaymentIcon />
        <NetellerPaymentIcon />
        <SkrillPaymentIcon />
      </>
    )
  }

  if (isTickmillUKType(tickmillEntity)) {
    return (
      <>
        <BankTransferPaymentIcon />
        <VisaPaymentIcon />
        <MasterCardPaymentIcon />
        <DotPayPaymentIcon />
        <NetellerPaymentIcon />
        <SkrillPaymentIcon />
        <PaySafePaymentIcon />
        <SofortPaymentIcon />
        <RapidPaymentIcon />
      </>
    )
  }

  return null
}

export const SignupFooter: React.FC = () => {
  const entity = useSessionEntity()
  const isArabic = useArabicSessionLanguage()

  return (
    <footer
      dir={isArabic ? 'rtl' : ''}
      className={classNames('footer', 'level', styles.signupFooter, styles.appFooter)}
      id='appFooter'
    >
      {!isTickmillUKType(entity) && entity !== TickmillCompaniesEnum.GENERIC && (
        <>
          <div className={styles.wrapper}>
            <div className='level-item'>
              <PaymentProviderLogoPerEntity />
            </div>
          </div>
        </>
      )}
      <RenderAppLinks />
    </footer>
  )
}

const RenderAppLinks: React.FC = () => {
  const entity = useSessionEntity()
  return (
    <nav
      aria-label='footer'
      className={classNames(
        styles.footerNav,
        !isTickmillUKType(entity) ? 'pt-0' : '',
        styles.wrapper
      )}
    >
      <AppLinksPerEntity />
    </nav>
  )
}

export const AppFooter: React.FC = () => {
  const isArabic = useArabicSessionLanguage()
  const location = useLocation()

  const [auth] = useContext(AuthSessionContext)

  if (auth) {
    return null
  }

  if (location.pathname.includes('/sign-up')) {
    return <SignupFooter />
  }

  return (
    <footer
      dir={isArabic ? 'rtl' : ''}
      id='appFooter'
      className={classNames('level', styles.appFooter)}
    >
      <nav className={classNames(styles.footerNav, styles.wrapper)} aria-label='footer'>
        <AppLinksPerEntity />
      </nav>
    </footer>
  )
}
const LocaleSpecificClause = ({ locale }: { locale: string }) => {
  const { t } = useTranslation()
  if (locale === 'pt') return <FooterText>{t('Footer.Clause')}</FooterText>
  return null
}

type UKFooterProducts = TickmillProductType.CFD | TickmillProductType.ETD

const useUKFooterProducts = (): UKFooterProducts[] => {
  const { account } = useAccountReadContext()
  const { selectedProductIds } = useProducts()

  const selectedUKProducts = selectedProductIds.filter(
    (productId): productId is UKFooterProducts =>
      productId === TickmillProductType.CFD || productId === TickmillProductType.ETD
  )

  if (selectedUKProducts.length > 0) {
    return selectedUKProducts
  }

  const hasAccount = !!account

  if (hasAccount) {
    const registeredProducts: UKFooterProducts[] = []

    if (isProductRegistered(account, TickmillProductType.CFD)) {
      registeredProducts.push(TickmillProductType.CFD)
    }

    if (isProductRegistered(account, TickmillProductType.ETD)) {
      registeredProducts.push(TickmillProductType.ETD)
    }

    if (registeredProducts.length > 0) {
      return registeredProducts
    }
  }

  return [TickmillProductType.CFD, TickmillProductType.ETD]
}
